import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export const Lightbox = ({ images, name, openIndex, onClose }) => {
  const [index, setIndex] = useState(openIndex ?? 0);

  useEffect(() => {
    setIndex(openIndex ?? 0);
  }, [openIndex]);

  const prev = useCallback(
    () => setIndex((i) => (i - 1 + images.length) % images.length),
    [images.length]
  );
  const next = useCallback(
    () => setIndex((i) => (i + 1) % images.length),
    [images.length]
  );

  useEffect(() => {
    if (openIndex === null || openIndex === undefined) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [openIndex, prev, next, onClose]);

  if (openIndex === null || openIndex === undefined) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex flex-col"
      onClick={onClose}
      data-testid="lightbox-overlay"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 lg:px-12 py-5 border-b border-zinc-900">
        <div>
          <div className="text-[10px] tracking-[0.3em] text-[#E50914] uppercase">⏤ Gallery</div>
          <div className="font-display text-lg uppercase tracking-tight mt-1">{name}</div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-xs font-mono-data text-zinc-500">
            {String(index + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 border border-zinc-800 hover:bg-[#E50914] hover:border-[#E50914] flex items-center justify-center transition-all"
            data-testid="lightbox-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main image */}
      <div
        className="flex-1 flex items-center justify-center px-4 md:px-16 py-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {images.length > 1 && (
          <button
            onClick={prev}
            data-testid="lightbox-prev-btn"
            className="absolute left-2 md:left-8 z-10 w-12 h-12 border border-zinc-800 bg-black/70 hover:bg-[#E50914] hover:border-[#E50914] flex items-center justify-center transition-all"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}
        <img
          src={images[index]}
          alt={`${name} ${index + 1}`}
          className="max-w-full max-h-full object-contain"
          data-testid={`lightbox-image-${index}`}
        />
        {images.length > 1 && (
          <button
            onClick={next}
            data-testid="lightbox-next-btn"
            className="absolute right-2 md:right-8 z-10 w-12 h-12 border border-zinc-800 bg-black/70 hover:bg-[#E50914] hover:border-[#E50914] flex items-center justify-center transition-all"
            aria-label="Next image"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div
          className="border-t border-zinc-900 px-6 lg:px-12 py-4 flex gap-3 overflow-x-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {images.map((src, i) => (
            <button
              key={src}
              onClick={() => setIndex(i)}
              data-testid={`lightbox-thumb-${i}`}
              className={`flex-shrink-0 w-24 h-16 border overflow-hidden transition-all ${
                i === index
                  ? "border-[#E50914] opacity-100"
                  : "border-zinc-800 opacity-50 hover:opacity-100"
              }`}
            >
              <img src={src} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
